import { getPulseInstanceIdByProductInstanceId } from "@/api/pulseInstanceAPI";
import { customersOptions, productInstancesOptions } from "@/api/queryApi";
import { useCompublish } from "@/hooks/contextHooks";
import useUser from "@/hooks/useUser";
import { useQueries, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import ProductBox from "./ProductBox";

export default function DisplayProducts() {
    const user = useUser();
    const { url } = useCompublish();
    const { t } = useTranslation();
    const { data: allProductInstances } = useQuery(productInstancesOptions());
    const productInstances = useMemo(() => {
        return allProductInstances?.filter(
            (instance) => instance.customerId === user?.ownedByCustomerId && !instance.deletedAt,
        );
    }, [allProductInstances, user]);
    const { data: customers } = useQuery(customersOptions());
    const customer = useMemo(
        () => customers?.find((customer) => customer.id === user?.ownedByCustomerId),
        [customers, user],
    );

    const pulseProductInstances = useMemo(
        () => productInstances?.filter((pi) => pi.portenType === "PULSE") ?? [],
        [productInstances],
    );

    const pulseInstanceResults = useQueries({
        queries: pulseProductInstances.map((pi) => ({
            queryKey: ["pulseInstanceId", pi.id],
            queryFn: () => getPulseInstanceIdByProductInstanceId(pi.id),
        })),
    });

    const pulseInstanceIdMap = useMemo(() => {
        const map = new Map<number, number>();
        pulseProductInstances.forEach((pi, i) => {
            const id = pulseInstanceResults[i]?.data?.id;
            if (id !== undefined) map.set(pi.id, id);
        });
        return map;
    }, [pulseProductInstances, ...pulseInstanceResults]);

    const productBoxes = useMemo(() => {
        return (
            productInstances?.map((productInstance) => {
                let link: string;
                if (productInstance.portenType === "PULSE") {
                    const pulseId = pulseInstanceIdMap.get(productInstance.id);
                    if (pulseId === undefined || !user?.ownedByCustomerId) return null;
                    return (
                        <ProductBox
                            key={productInstance.id}
                            title={productInstance.name}
                            to="/$customerId/pulse/$pulseId"
                            params={{
                                customerId: String(user.ownedByCustomerId),
                                pulseId: String(pulseId),
                            }}
                        />
                    );
                } else if (productInstance.portenType) {
                    link = `/`;
                } else if (productInstance.compublishId) {
                    link = `${url}/${customer?.url}/${productInstance.url}`;
                } else {
                    link = productInstance.url;
                }

                return (
                    <ProductBox key={productInstance.id} title={productInstance.name} href={link} />
                );
            }) ?? []
        );
    }, [productInstances, customer, url, pulseInstanceIdMap, user?.ownedByCustomerId]);

    return (
        <>
            <div className="grid grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-8 w-fit">
                {productBoxes.length > 0 ? (
                    productBoxes
                ) : (
                    <div className="text-on-bg-image text-lg">
                        {t("products.noProductsAvailable")}
                    </div>
                )}
            </div>
        </>
    );
}
